import React from 'react';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  SparkForm,
  SparkTextField,
  SparkFormButton,
  SparkPasswordField,
} from 'sharedComponents/SparkForm';
import { useLogin } from 'hooks/auth/useLogin';
import { QUESTION_MARK_LEADING_PATTERN } from 'constants/regex';

type LoginFormType = {
  email: string;
  password: string;
};

const initialValues = {
  email: '',
  password: '',
};
const validationSchema = Yup.object({
  email: Yup.string()
    .required('Email is Required')
    .email('Please enter a valid Email'),
  password: Yup.string().required('Password is Required'),
});

type LoginFormProps = {
  onForgetPasswordClick?: () => void;
  callBack?: () => void;
  modalView?: boolean;
  showLogo?: boolean;
};

function LoginForm({
  callBack,
  modalView,
  showLogo = true,
  onForgetPasswordClick,
}: LoginFormProps) {
  const { mutateAsync: login, isLoading } = useLogin();
  const passwordRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { search, state } = useLocation();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      passwordRef.current?.focus();
    }
  };

  const handleLogin = async (loginCredentials: LoginFormType) => {
    const res = await login(loginCredentials);
    if (res.isSuccess === true && res.data.userID) {
      if (callBack) {
        callBack();
        return;
      }
      if (state?.from) {
        navigate(state?.from);
        return;
      }
      const redirectURL = search.replace(QUESTION_MARK_LEADING_PATTERN, '');
      navigate(redirectURL || '/', { replace: true });
    }
  };

  return (
    <SparkForm<LoginFormType>
      initialValues={initialValues}
      onSave={handleLogin}
      validationSchema={validationSchema}
    >
      <SparkTextField
        name='email'
        label='Email'
        size={12}
        placeholder='Enter Email'
        disabled={isLoading}
        onKeyDown={handleKeyDown}
        autoLowerCase={true}
      />

      <SparkPasswordField
        name='password'
        label='Password'
        placeholder='Enter Password'
        ref={passwordRef}
        size={12}
        modalView={modalView}
        showLogo={showLogo}
        onForgetPasswordClick={onForgetPasswordClick}
      />

      <SparkFormButton
        disabled={isLoading}
        sx={{
          padding: 2,
          textDecoration: 'none',
          fontSize: '14px',
        }}
      >
        {isLoading ? 'Loading' : 'Login'}
      </SparkFormButton>
    </SparkForm>
  );
}

export default LoginForm;
